/**
 * 컨소시엄(협업 인원) 확정 공통 팝업
 * - 상단: 과제명(자동 세팅), Alliance Space 이름 입력
 * - 좌측: 현재 참여 중인 기관·담당자 카드 (드래그 또는 화살표로 추가)
 * - 가운데: 좌우 화살표 버튼 (→ 추가, ← 제거)
 * - 우측: 확정할 기관·담당자 드롭 영역 (초기 비움)
 * - config.initialConsortium, config.projectTitle, config.allianceName
 * - 확인 시 onClose(true, { consortium, projectTitle, allianceName }) 호출
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';

const DRAG_TYPE = 'application/x-consortium-item';

/** 좌측 "현재 참여 중인 기관·담당자" 목업 (config.initialConsortium 미제공 시 사용) - 기술수요/연구사업 기관·전문가 */
const MOCK_CONSORTIUM_POOL = [
  { organization: '한국AI연구소', person: '김연구' },
  { organization: '바이오헬스랩', person: '이바이오' },
  { organization: '그린케미칼', person: '박소재' },
  { organization: '㈜테크솔루션', person: '최담당' },
  { organization: '소재연구소', person: '정실장' },
  { organization: '디지털헬스케어센터', person: '강팀장' },
  { organization: '김연구소', person: '김연구' },
  { organization: '차세대배터리연구단', person: '윤책임' },
  { organization: '스마트팩토리연합', person: '한대리' },
  { organization: '국가R&D평가원', person: '오연구원' },
];

/** Alliance Space 이름 기본값: "과제명 Alliance Space 현재날짜+유니크인덱스" */
const getDefaultAllianceName = (taskName, translate) => {
  const taskText = String(taskName ?? '').trim();
  const now = new Date();
  const dateStr = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('');
  const uniqueIndex = String(Math.abs(Date.now() % 100000)).padStart(5, '0');
  return taskText
    ? translate('popup.consortiumSelect.defaultAllianceNameWithTask', { task: taskText, date: dateStr, index: uniqueIndex })
    : translate('popup.consortiumSelect.defaultAllianceName', { date: dateStr, index: uniqueIndex });
};

const ConsortiumSelectPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const initialList = useMemo(() => {
    const fromConfig = Array.isArray(config?.initialConsortium) ? config.initialConsortium : [];
    const key = (item) => `${String(item?.organization ?? '').trim()}|${String(item?.person ?? '').trim()}`;
    const existingKeys = new Set(fromConfig.filter((i) => (i?.organization ?? '').trim()).map(key));
    const mockOnly = MOCK_CONSORTIUM_POOL.filter((m) => !existingKeys.has(key(m)));
    return [...fromConfig, ...mockOnly];
  }, [config?.initialConsortium]);
  const pool = useState(() =>
    initialList.map((item) => ({
      id: `pool-${Math.random().toString(36).slice(2)}`,
      organization: String(item?.organization ?? '').trim() || '',
      person: String(item?.person ?? '').trim() || '',
    }))
  )[0];
  const [confirmed, setConfirmed] = useState([]);
  const [projectTitle, setProjectTitle] = useState(config?.projectTitle ?? '');
  const [allianceName, setAllianceName] = useState(config?.allianceName ?? '');
  const [dragSource, setDragSource] = useState(null);
  const [dropZoneOver, setDropZoneOver] = useState(false);
  const [selectedPoolIndex, setSelectedPoolIndex] = useState(null);
  const [selectedConfirmedIndex, setSelectedConfirmedIndex] = useState(null);

  useEffect(() => {
    const title = config?.projectTitle != null && config.projectTitle !== '' ? String(config.projectTitle) : '';
    const customAlliance = String(config?.allianceName ?? '').trim();
    if (title) {
      setProjectTitle(title);
      if (!customAlliance) {
        setAllianceName(getDefaultAllianceName(title, t));
      }
    }
    if (customAlliance) {
      setAllianceName(customAlliance);
    }
  }, [config?.projectTitle, config?.allianceName, t]);

  const makeItem = useCallback((item) => ({
    organization: String(item?.organization ?? '').trim(),
    person: String(item?.person ?? '').trim(),
    id: `item-${Math.random().toString(36).slice(2)}`,
  }), []);

  const isDuplicateInConfirmed = useCallback((currentConfirmed, org, person) => {
    const o = String(org ?? '').trim();
    const p = String(person ?? '').trim();
    return currentConfirmed.some(
      (c) => String(c?.organization ?? '').trim() === o && String(c?.person ?? '').trim() === p
    );
  }, []);

  const handleDragStartPool = useCallback((e, index) => {
    const item = pool[index];
    if (!item?.organization) return;
    setDragSource('pool');
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ organization: item.organization, person: item.person }));
    e.dataTransfer.effectAllowed = 'copy';
  }, [pool]);

  const handleDragStartConfirmed = useCallback((e, index) => {
    const item = confirmed[index];
    if (!item?.organization) return;
    setDragSource('confirmed');
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ organization: item.organization, person: item.person, index }));
    e.dataTransfer.effectAllowed = 'move';
  }, [confirmed]);

  const handleDragEnd = useCallback(() => {
    setDragSource(null);
    setDropZoneOver(false);
  }, []);

  const handleDropOnConfirmed = useCallback((e) => {
    e.preventDefault();
    setDropZoneOver(false);
    const raw = e.dataTransfer.getData(DRAG_TYPE);
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      if (dragSource === 'pool') {
        const org = String(payload?.organization ?? '').trim();
        const person = String(payload?.person ?? '').trim();
        if (org) {
          setConfirmed((prev) => {
            if (isDuplicateInConfirmed(prev, org, person)) return prev;
            return [...prev, makeItem(payload)];
          });
        }
      } else if (dragSource === 'confirmed' && typeof payload.index === 'number') {
        setConfirmed((prev) => {
          const next = [...prev];
          const [removed] = next.splice(payload.index, 1);
          next.push(removed);
          return next;
        });
      }
    } catch (err) {
      // ignore
    }
    setDragSource(null);
  }, [dragSource, makeItem, isDuplicateInConfirmed]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragSource === 'pool' ? 'copy' : 'move';
  }, [dragSource]);

  const handleDragEnterZone = useCallback(() => setDropZoneOver(true), []);
  const handleDragLeaveZone = useCallback(() => setDropZoneOver(false), []);

  const removeFromConfirmed = useCallback((index) => {
    setConfirmed((prev) => prev.filter((_, i) => i !== index));
    setSelectedConfirmedIndex(null);
  }, []);

  const addPoolToConfirmed = useCallback(() => {
    if (selectedPoolIndex == null) return;
    const item = pool[selectedPoolIndex];
    if (!item?.organization) return;
    setConfirmed((prev) => {
      if (isDuplicateInConfirmed(prev, item.organization, item.person)) return prev;
      return [...prev, makeItem(item)];
    });
    setSelectedPoolIndex(null);
  }, [selectedPoolIndex, pool, makeItem, isDuplicateInConfirmed]);

  const removeSelectedFromConfirmed = useCallback(() => {
    if (selectedConfirmedIndex == null) return;
    setConfirmed((prev) => prev.filter((_, i) => i !== selectedConfirmedIndex));
    setSelectedConfirmedIndex(null);
  }, [selectedConfirmedIndex]);

  const handleConfirm = useCallback(() => {
    const consortium = confirmed.filter((item) => item.organization.length > 0).map(({ organization, person }) => ({ organization, person }));
    onClose(true, {
      consortium,
      projectTitle: projectTitle.trim(),
      allianceName: allianceName.trim(),
    });
  }, [confirmed, projectTitle, allianceName, onClose]);

  const handleCancel = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const validConfirmed = confirmed.filter((item) => item.organization.length > 0);
  const canConfirm = validConfirmed.length > 0;

  return (
    <PopupWrapper
      config={{
        ...config,
        title: config?.title ?? t('popup.consortiumSelect.title'),
        minWidth: '720px',
        maxWidth: config?.maxWidth ?? '900px',
        minHeight: '420px',
        width: '100%',
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-consortium-select"
    >
      <div className="consortium-select-popup-body">
        {/* 상단: 과제명 / Alliance Space 이름 (위아래 구성) */}
        <div className="consortium-select-top row g-0 mb-3">
          <div className="col-12">
            <label className="form-label small fw-semibold">{t('popup.consortiumSelect.projectTitleLabel')}</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t('popup.consortiumSelect.projectTitlePlaceholder')}
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
            />
          </div>
          <div className="col-12">
            <label className="form-label small fw-semibold">{t('popup.consortiumSelect.allianceNameLabel')}</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t('popup.consortiumSelect.allianceNamePlaceholder')}
              value={allianceName}
              onChange={(e) => setAllianceName(e.target.value)}
            />
          </div>
        </div>

        {/* 좌우 구성: 제목 영역과 여백 */}
        <div className="consortium-select-panels row g-3 mt-4">
          {/* 좌측: 현재 참여 중인 기관·담당자 카드 */}
          <div className="col-12 col-md-5 order-1">
            <div className="consortium-panel-label small fw-semibold text-muted mb-2">{t('popup.consortiumSelect.currentParticipantsLabel')}</div>
            <div className="consortium-pool-cards border rounded p-2 bg-light consortium-panel-scroll" style={{ height: 220 }}>
              {pool.filter((i) => i.organization).length === 0 ? (
                <div className="text-muted small py-4 text-center">{t('popup.consortiumSelect.emptyPool')}</div>
              ) : (
                <div className="consortium-pool-cards-grid">
                  {pool.map((item, index) =>
                    item.organization ? (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={(e) => handleDragStartPool(e, index)}
                        onDragEnd={handleDragEnd}
                        onClick={() => { setSelectedPoolIndex(index); setSelectedConfirmedIndex(null); }}
                        className={`consortium-card consortium-card-draggable border rounded p-2 bg-white shadow-sm d-flex align-items-center gap-2 ${selectedPoolIndex === index ? 'consortium-card-selected' : ''}`}
                      >
                        <div className="consortium-card-avatar" aria-hidden="true">
                          {(item.person && item.person.trim()) ? item.person.trim().slice(0, 1) : (item.organization && item.organization.trim()) ? item.organization.trim().slice(0, 1) : '?'}
                        </div>
                        <div className="min-width-0 flex-grow-1">
                          <div className="fw-medium small text-break">{item.organization}</div>
                          <div className="text-muted consortium-card-person">{item.person || '-'}</div>
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 가운데: 좌우 화살표 - 모바일에서 가로 배치 */}
          <div className="col-12 col-md-2 order-2 d-flex flex-row flex-md-column align-items-center justify-content-center gap-2 consortium-arrow-wrap py-2 py-md-0">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm consortium-arrow-btn"
              onClick={addPoolToConfirmed}
              disabled={selectedPoolIndex == null}
              title={t('popup.consortiumSelect.addToConfirmedTitle')}
            >
              →
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm consortium-arrow-btn"
              onClick={removeSelectedFromConfirmed}
              disabled={selectedConfirmedIndex == null}
              title={t('popup.consortiumSelect.removeFromConfirmedTitle')}
            >
              ←
            </button>
          </div>

          {/* 우측: 확정할 기관·담당자 드롭 영역 */}
          <div className="col-12 col-md-5 order-3">
            <div className="consortium-panel-label small fw-semibold text-muted mb-2">{t('popup.consortiumSelect.confirmedParticipantsLabel')}</div>
            <div
              className={`consortium-drop-zone border rounded border-2 border-dashed p-3 bg-white consortium-panel-scroll ${dropZoneOver ? 'drag-over' : ''}`}
              style={{ height: 220, overflowY: 'auto' }}
              onDrop={handleDropOnConfirmed}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnterZone}
              onDragLeave={handleDragLeaveZone}
            >
              {validConfirmed.length === 0 ? (
                <div className="consortium-drop-placeholder text-muted small py-4 text-center">
                  {t('popup.consortiumSelect.dropPlaceholder')}
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {confirmed.map((item, index) =>
                    item.organization ? (
                      <div
                        key={item.id || index}
                        draggable
                        onDragStart={(e) => handleDragStartConfirmed(e, index)}
                        onDragEnd={handleDragEnd}
                        onClick={() => { setSelectedConfirmedIndex(index); setSelectedPoolIndex(null); }}
                        className={`consortium-card consortium-card-in-confirmed border rounded p-2 bg-white shadow-sm d-flex align-items-center gap-2 ${selectedConfirmedIndex === index ? 'consortium-card-selected' : ''}`}
                      >
                        <div className="consortium-card-avatar" aria-hidden="true">
                          {(item.person && item.person.trim()) ? item.person.trim().slice(0, 1) : (item.organization && item.organization.trim()) ? item.organization.trim().slice(0, 1) : '?'}
                        </div>
                        <div className="min-width-0 flex-grow-1">
                          <div className="fw-medium small text-break">{item.organization}</div>
                          <div className="text-muted consortium-card-person">{item.person || '-'}</div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0 text-decoration-none"
                          onClick={(e) => { e.stopPropagation(); removeFromConfirmed(index); }}
                          aria-label={t('popup.common.remove')}
                        >
                          ×
                        </button>
                      </div>
                    ) : null
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="consortium-select-popup-footer d-flex flex-wrap justify-content-end gap-2 mt-3 pt-3 border-top">
        <button type="button" className={`btn ${getButtonClass('btn-secondary')} consortium-footer-btn`} onClick={handleCancel}>
          {t('popup.common.cancel')}
        </button>
        <button type="button" className={`btn ${getButtonClass('btn-primary')} consortium-footer-btn`} onClick={handleConfirm} disabled={!canConfirm}>
          {t('popup.common.confirm')}
        </button>
      </div>

      <style>{`
        .popup-consortium-select .consortium-card-draggable,
        .popup-consortium-select .consortium-card-in-confirmed {
          cursor: grab;
        }
        .popup-consortium-select .consortium-card-draggable:active,
        .popup-consortium-select .consortium-card-in-confirmed:active {
          cursor: grabbing;
        }
        .popup-consortium-select .consortium-card-selected {
          border-color: var(--bs-primary, #0d6efd) !important;
          box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
        }
        .popup-consortium-select .consortium-drop-zone.drag-over {
          background: rgba(13, 110, 253, 0.06) !important;
          border-color: #0d6efd !important;
        }
        .popup-consortium-select .consortium-arrow-btn {
          min-width: 2.5rem;
          font-size: 1.1rem;
        }
        .popup-consortium-select .consortium-card-in-confirmed .btn-link.text-decoration-none {
          text-decoration: none !important;
        }
        .popup-consortium-select .consortium-card-in-confirmed .btn-link.text-decoration-none:hover {
          text-decoration: none !important;
        }
        .popup-consortium-select .consortium-card-person {
          font-size: 12px;
        }
        /* 기관·담당자 카드 왼쪽 동그라미 프로필 */
        .popup-consortium-select .consortium-card-avatar {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          min-width: 36px;
          min-height: 36px;
          border-radius: 50%;
          background: var(--theme-color-primary, #0d6efd);
          color: var(--theme-color-on-primary, #fff);
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }
        .popup-consortium-select .text-break {
          word-break: break-word;
          overflow-wrap: break-word;
        }
        /* 좌측 현재 참여 중인 기관·담당자: 세로 스크롤 + 2컬럼 */
        .popup-consortium-select .consortium-pool-cards {
          overflow-y: auto;
          max-height: 320px;
          -webkit-overflow-scrolling: touch;
        }
        .popup-consortium-select .consortium-pool-cards-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        /* 모바일 반응형 */
        @media (max-width: 767.98px) {
          .popup-consortium-select {
            min-width: 0 !important;
            width: 100% !important;
            max-width: calc(100vw - 24px) !important;
            margin: 12px;
          }
          .popup-consortium-select .consortium-select-popup-body {
            overflow-x: hidden;
          }
          .popup-consortium-select .consortium-select-top .form-control {
            font-size: 16px;
          }
          .popup-consortium-select .consortium-pool-cards,
          .popup-consortium-select .consortium-drop-zone {
            min-height: 180px;
            max-height: 40vh;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }
          .popup-consortium-select .consortium-arrow-wrap {
            flex-direction: row;
            min-height: auto;
          }
          .popup-consortium-select .consortium-arrow-btn {
            min-width: 44px;
            min-height: 44px;
            padding: 0.5rem;
          }
          .popup-consortium-select .consortium-select-popup-footer {
            flex-wrap: wrap;
          }
          .popup-consortium-select .consortium-footer-btn {
            min-height: 44px;
            flex: 1 1 auto;
            min-width: 120px;
          }
        }
      `}</style>
    </PopupWrapper>
  );
};

export default ConsortiumSelectPopup;
